import {
  // ArrowLeftOutlined,
  CheckOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { Button, Modal, Divider, message } from 'antd';
import { useState, useEffect, useMemo } from 'react';
import { useCreateSubscription, useCheckoutQuote } from '@/lib/api/hooks/useCreateSubscription';
import CouponInput from '@/components/common/CouponInput';
import { subscriptionplanService } from '@/lib/api/services/razorpaySubscription-service';
import type { PlanChangePreview } from '@/lib/api/services/razorpaySubscription-service';
import { useActivePlans } from '@/lib/api/hooks/usePlans';

import type {
  DynamicPlan,
  PlanAuthorization,
  PlanFeatureDescription,
  SubscriptionCycle,
} from '@/types/profile';
import { toApiDate } from '@/utility/to-api-date';
import { formatMoney } from '@/lib/utils/currency';

interface PlanSelectionModalProps {
  visible: boolean;
  onClose: () => void;
  onPaymentSuccess?: () => void;
  /** Show only this tier, e.g. 'growth' from the ads-revenue limit page. */
  onlyTier?: string;
}

interface PlanInfo {
  name: string;
  cycle: SubscriptionCycle;
  price: number;
  originalPrice?: number;
  discount?: string;
  billingDate: string;
  plan_id: string;
  currency: string;
  authorization: PlanAuthorization;
  planFeatureDescriptions: PlanFeatureDescription[];
  identifier: SubscriptionCycle;
  save: number;
}

export default function PlanSelectionModal({
  visible,
  onClose,
  onPaymentSuccess,
  onlyTier,
}: PlanSelectionModalProps) {
  const {
    data: plansResponse,
    isLoading: plansLoading,
    error: plansError,
  } = useActivePlans();
  const dynamicPlans = useMemo(() => {
    const plans = ((plansResponse as any)?.data || []).filter(
      (plan: any) => !onlyTier || plan.planTier === onlyTier
    );
    // Each currency is its own plan row granting the same services, but copy is
    // usually written on the INR row only. A row without copy borrows it from
    // its same-tier, same-cycle sibling.
    return plans.map((plan: any) => {
      if (!plan.planTier || (plan.description && plan.planFeatureDescriptions?.length)) {
        return plan;
      }
      const sibling = plans.find(
        (p: any) =>
          p !== plan &&
          p.planTier === plan.planTier &&
          p.identifier === plan.identifier &&
          (p.description || p.planFeatureDescriptions?.length)
      );
      if (!sibling) return plan;
      return {
        ...plan,
        description: plan.description || sibling.description,
        planFeatureDescriptions: plan.planFeatureDescriptions?.length
          ? plan.planFeatureDescriptions
          : sibling.planFeatureDescriptions,
      };
    });
  }, [plansResponse, onlyTier]);



  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [subscriptionCycle, setSubscriptionCycle] = useState<SubscriptionCycle>('yearly');
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD');

  const currenciesWithPlans = useMemo(() => {
    const set = new Set<string>();
    dynamicPlans.forEach((plan: any) => {
      if (plan.currency) {
        set.add(plan.currency.toUpperCase());
      }
    });
    return Array.from(set);
  }, [dynamicPlans]);

  // USD is the default, but with no USD plans in the catalog it would open on an empty grid.
  useEffect(() => {
    if (currenciesWithPlans.length && !currenciesWithPlans.includes(selectedCurrency)) {
      setSelectedCurrency(currenciesWithPlans[0]);
    }
  }, [currenciesWithPlans, selectedCurrency]);

  const showCurrencyToggle = currenciesWithPlans.length > 1;

  const [appliedCouponCode, setAppliedCouponCode] = useState<string | undefined>(undefined);

  /**
   * The plan the customer is on now, kept separate from what they have picked.
   *
   * Without it the modal cannot tell "buy this" from "move to this", and it
   * pre-selects the active plan, so the button would offer to sell someone the
   * plan they already have.
   */
  const [currentPlanId, setCurrentPlanId] = useState<number | null>(null);

  /** What moving to the selected plan would cost and when it would happen. */
  const [preview, setPreview] = useState<PlanChangePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  /**
   * Generated once per press and reused on retry, so the server can recognise a
   * repeat of the same action. The triple-fired error came from three requests
   * being sent for one click.
   */
  const [changeRequestKey, setChangeRequestKey] = useState<string | null>(null);

  /** True from the moment the button is pressed until the tab opens. */
  const [submitting, setSubmitting] = useState(false);

  /** Whether the customer already pays for something, and is therefore moving. */
  const isPlanChange = currentPlanId !== null;

  const {
    data: quote,
    isLoading: isQuoteLoading,
    isError: isQuoteError,
    refetch: refetchQuote,
  } = useCheckoutQuote(selectedPlan, appliedCouponCode);

  /**
   * Whether the server can price this purchase at all.
   *
   * Checked at runtime rather than behind a build flag: if quoting is
   * unavailable the customer can still buy at the plan's own price, and the
   * richer flow starts working the moment the endpoint answers — no redeploy.
   */
  const canQuote = !isQuoteError;

  // Helper function to get current plan info
  const getCurrentPlanInfo = (): PlanInfo | null => {
    if (!selectedPlan) return null;

    const plan = dynamicPlans.find(
      (p) =>
        p.id === selectedPlan &&
        p.identifier === subscriptionCycle &&
        (p.currency || 'INR').toUpperCase() === selectedCurrency.toUpperCase()
    );
    if (!plan) return null;
    return {
      name: plan.planName,
      cycle: plan.identifier,
      price: plan.amount,
      originalPrice: plan.displayAmount || plan.amount,
      billingDate: (() => {
        const date = new Date();
        const cycleMap = { monthly: 1, quarterly: 3, yearly: 12 };
        date.setMonth(date.getMonth() + cycleMap[subscriptionCycle]);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
        });
      })(),
      plan_id: plan.id.toString(),
      currency: plan.currency,
      authorization: plan.authorization,
      planFeatureDescriptions: plan.planFeatureDescriptions || [],
      identifier: plan.identifier,
      save: plan.save ?? 0,
    };
  };



  const currentPlanInfo = getCurrentPlanInfo();

  useEffect(() => {
    const findPlanById = (
      planId: string
    ): {
      selectedPlan: number;
      subscriptionCycle: SubscriptionCycle;
      currency?: string;
    } | null => {
      const plan = dynamicPlans.find((p) => p.id.toString() === planId);
      if (plan) {
        return {
          selectedPlan: plan.id,
          subscriptionCycle: plan.identifier,
          currency: plan.currency,
        };
      }
      return null;
    };

    const fetchActiveSubscription = async () => {
      try {
        const response =
          await subscriptionplanService.getActiveSubscriptionProfiles();
        const activePlan = response?.data?.plan;
        const activeCurrency = activePlan?.currency;
        if (activeCurrency) {
          setSelectedCurrency(activeCurrency.toUpperCase());
        }
        const activePlanId = activePlan?.id?.toString();
        if (!activePlanId) return;
        const match = findPlanById(activePlanId);
        if (match) {
          // Remembered, not selected. The customer opened this to move plans,
          // so their current one is context rather than a choice — the cycle
          // still opens on theirs so the comparison starts somewhere sensible.
          setCurrentPlanId(match.selectedPlan);
          setSubscriptionCycle(match.subscriptionCycle);
          if (match.currency) {
            setSelectedCurrency(match.currency.toUpperCase());
          }
        }
      } catch (error) {
        console.error('Failed to fetch active subscription:', error);
      }
    };

    fetchActiveSubscription();
  }, [dynamicPlans]);

  const handleCycleChange = (cycle: SubscriptionCycle) => {
    setSubscriptionCycle(cycle);
    setSelectedPlan(null);
    setAppliedCouponCode(undefined);
    setPreview(null);
  };

  const handleCurrencyChange = (currency: string) => {
    setSelectedCurrency(currency);
    setSelectedPlan(null);
    setAppliedCouponCode(undefined);
    setPreview(null);
  };

  /**
   * Asks the server what the selected plan would mean.
   *
   * The direction and the price both come from the server: the customer is
   * shown what will actually be charged, not a figure the browser worked out.
   */
  useEffect(() => {
    if (!isPlanChange || !selectedPlan || selectedPlan === currentPlanId) {
      setPreview(null);
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);

    subscriptionplanService
      .getPlanChangePreview(selectedPlan)
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch(() => {
        // A preview the server will not give is not a reason to block the
        // screen; the change itself will refuse with a readable message.
        if (!cancelled) setPreview(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedPlan, currentPlanId, isPlanChange]);

  const handlePlanSelect = (planId: number) => {
    setSelectedPlan(planId);
    setAppliedCouponCode(undefined);
  };

  const createSubscription = useCreateSubscription();

  /**
   * Moves an existing subscriber to the selected plan.
   *
   * An upgrade opens a payment page; anything else is booked for the end of the
   * paid period and takes no money today. Either way the customer keeps what
   * they are paying for until the change actually takes effect.
   */
  const handlePlanChange = async () => {
    if (!selectedPlan || submitting) return;

    // One key per press, reused if this runs again, so a repeat is recognised
    // as the same action rather than charged as a second one.
    const key =
      changeRequestKey ??
      (globalThis.crypto?.randomUUID?.() ??
        `chg-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    setChangeRequestKey(key);
    setSubmitting(true);

    try {
      const result = await subscriptionplanService.createPlanChange({
        planId: selectedPlan,
        quoteId: quote?.quoteId,
        changeRequestKey: key,
      });

      if (result.redirectUrl) {
        window.open(result.redirectUrl, '_blank');
        onPaymentSuccess?.();
        onClose();
        return;
      }

      // No link means nothing to pay today — a booked change.
      message.success(
        result.message ??
          `Your plan will change on ${result.effectiveDate}.`,
      );
      onPaymentSuccess?.();
      onClose();
    } catch (error: any) {
      const text =
        error?.response?.data?.message ??
        error?.message ??
        'Could not change your plan. Please try again.';
      message.error(text);
      // Cleared so the next attempt is a fresh action rather than a replay of
      // one the server already rejected.
      setChangeRequestKey(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubscribe = async () => {
    if (submitting) return;
    if (!currentPlanInfo) return;
    // Without a quote the purchase still goes through at the plan's own price.
    if (canQuote && !quote) return;

    // An existing subscriber is moving plans, not buying one.
    if (isPlanChange && selectedPlan !== currentPlanId) {
      return handlePlanChange();
    }

    if (quote && new Date(quote.expiresAt) <= new Date()) {
      const oldNet = quote.net;
      const { data: newQuote } = await refetchQuote();
      if (newQuote?.net !== oldNet) {
        message.warning('Quote expired and price has changed. Please review the new amount before subscribing.');
        return;
      }
    }

    try {
      const planId = parseInt(currentPlanInfo.plan_id);
      const startDate = toApiDate(new Date());
      const endDate = (() => {
        const date = new Date();
        const cycleMap = { monthly: 1, quarterly: 3, yearly: 12 };
        date.setMonth(date.getMonth() + cycleMap[subscriptionCycle]);
        return toApiDate(date);
      })();

      const payload: any = {
        planId: planId,
        startDate: startDate,
        endDate: endDate,
        createdById: 0,
        updatedById: 0,
        isActive: true,
      };

      // The quote is what carries the discounted price to the gateway. Omitted,
      // the server charges the plan price through the recurring path.
      if (quote?.quoteId) {
        payload.quoteId = quote.quoteId;
      }

      createSubscription.mutate(payload, {
        onSuccess: (response) => {
          // Check if response indicates an error (like active subscription exists)
          if (
            response?.response?.success === false ||
            response?.success === false
          ) {
            const errorMessage =
              response?.response?.message ||
              response?.message ||
              'An error occurred';
            message.error(errorMessage);
            return;
          }

          const paymentLink =
            response.data?.redirectUrl || response.payment_link;
          if (paymentLink) {
            window.open(paymentLink, '_blank');
            onPaymentSuccess?.();
          } else {
            console.error('No payment link found in response:', response);
            if (response.error) {
              message.error(response.error);
            }
          }
        },
        onError: (error: any) => {
          console.error('Subscription creation failed:', error);

          // Extract error message from API response
          let errorMessage = 'Failed to create subscription. Please try again.';

          if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error?.message) {
            errorMessage = error.message;
          }

          message.error(errorMessage);
        },
      });
    } catch (error: any) {
      console.error(error);

      // Extract error message from API response
      let errorMessage = 'An error occurred. Please try again.';

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      message.error(errorMessage);
    }
  };

  const getPlanCardClasses = (planId: number) => {
    const baseClasses =
      'relative bg-white rounded-xl p-6 border-2 transition-all duration-200 hover:shadow-lg cursor-pointer';

    if (selectedPlan === planId) {
      return `${baseClasses} border-amber-500 shadow-md`;
    }
    return `${baseClasses} border-gray-200 hover:border-gray-300`;
  };

  const getButtonClasses = (planId: number) => {
    const baseClasses =
      'w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 cursor-pointer';

    if (selectedPlan === planId) {
      return `${baseClasses} bg-amber-600 hover:bg-amber-700 !text-white`;
    }

    return `${baseClasses} border-2 border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50 bg-white`;
  };

  const getButtonText = (plan: DynamicPlan) => {
    if (plan.id === currentPlanId) {
      return 'Your current plan';
    }
    if (selectedPlan === plan.id) {
      return 'Selected';
    }
    return `Select ${plan.planName}`;
  };



  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1400}
      className="rounded-2xl"
      style={{ top: 20 }}
    >
      <div className="bg-gray-50 -m-6 p-6 min-h-[80vh]">
        {/* Header */}
        <div className="mb-8">
          {/* <Link
            to="/dashboard/profile/billing"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeftOutlined />
            Back to Billing
          </Link> */}

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Upgrade Your Plan Today
            </h1>

            {/* Cycle and Currency Toggles */}
            <div className="flex items-center justify-center gap-4 mt-4 flex-wrap">
              {/* Billing Toggle */}
              <div className="inline-flex bg-gray-200 p-1 rounded-lg">
                <button
                  type="button"
                  onClick={() => handleCycleChange('monthly')}
                  className={`px-4 py-2 rounded-md font-medium transition-all cursor-pointer ${
                    subscriptionCycle === 'monthly'
                      ? 'bg-amber-600 !text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => handleCycleChange('yearly')}
                  className={`px-4 py-2 rounded-md font-medium transition-all cursor-pointer ${
                    subscriptionCycle === 'yearly'
                      ? 'bg-amber-600 !text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Annually
                </button>
              </div>

              {/* Currency Toggle */}
              {showCurrencyToggle && (
                <div className="inline-flex bg-gray-200 p-1 rounded-lg" data-testid="currency-toggle">
                  <button
                    type="button"
                    onClick={() => handleCurrencyChange('USD')}
                    className={`px-4 py-2 rounded-md font-medium transition-all cursor-pointer ${
                      selectedCurrency === 'USD'
                        ? 'bg-amber-600 !text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    USD ($)
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCurrencyChange('INR')}
                    className={`px-4 py-2 rounded-md font-medium transition-all cursor-pointer ${
                      selectedCurrency === 'INR'
                        ? 'bg-amber-600 !text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    INR (₹)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Plans Grid */}
          <div className="flex-1">
            {plansLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-gray-600">Loading plans...</div>
              </div>
            ) : plansError ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-red-600">
                  Error loading plans. Please try again.
                </div>
              </div>
            ) : (
              (() => {
                  const filteredPlans = dynamicPlans
                    .filter(
                      (plan) =>
                        plan.identifier === subscriptionCycle &&
                        (plan.currency || 'INR').toUpperCase() === selectedCurrency.toUpperCase()
                    )
                    .sort((a, b) => a.id - b.id);

                  const gridCols =
                    filteredPlans.length === 1
                      ? 'md:grid-cols-1 max-w-md mx-auto'
                      : filteredPlans.length === 2
                        ? 'md:grid-cols-2 max-w-3xl mx-auto'
                        : 'md:grid-cols-3';

                  if (filteredPlans.length === 0) {
                    return (
                      <div className="grid grid-cols-1 gap-6 mb-8">
                        <div className="col-span-full flex justify-center items-center h-64">
                          <div className="text-center">
                            <div className="text-gray-500 text-lg mb-2">
                              No plans found
                            </div>
                            <div className="text-gray-400 text-sm">
                              No {subscriptionCycle} plans are currently available
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className={`grid grid-cols-1 ${gridCols} gap-6 mb-8`}>
                      {filteredPlans.map((plan) => {
                        const mainDescriptions = (
                          plan.planFeatureDescriptions || []
                        ).filter((d) => d.descriptionType === 'main');
                        const addonDescriptions = (
                          plan.planFeatureDescriptions || []
                        ).filter((d) => d.descriptionType === 'addon');
                        const savings =
                          plan.displayAmount && plan.displayAmount > plan.amount
                            ? ((plan.displayAmount - plan.amount) /
                                plan.displayAmount) *
                              100
                            : 0;
                        const billingText =
                          plan.identifier === 'yearly'
                            ? `Billed annually`
                            : plan.identifier === 'quarterly'
                            ? `Billed quarterly`
                            : `Billed monthly`;
                            
                        const cycleSuffix = plan.identifier === 'yearly' ? 'year' : plan.identifier === 'quarterly' ? 'quarter' : 'month';
                        const cycleMonths = plan.identifier === 'yearly' ? 12 : plan.identifier === 'quarterly' ? 3 : 1;
                        const perMonthEquiv = Math.round(plan.amount / cycleMonths);

                        return (
                          <div
                            key={plan.id}
                            className={getPlanCardClasses(plan.id)}
                            onClick={() => handlePlanSelect(plan.id)}
                          >
                            {/* Badge */}
                            {plan.badge && (
                              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                <span className="bg-amber-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                                  {plan.badge}
                                </span>
                              </div>
                            )}

                            {/* Current plan marker, so the customer can see
                                what they are moving from. */}
                            {plan.id === currentPlanId && (
                              <div className="absolute top-4 right-4 bg-gray-700 text-white px-3 py-1 rounded-full text-xs font-medium">
                                Current
                              </div>
                            )}

                            {/* Selected Badge */}
                            {selectedPlan === plan.id && plan.id !== currentPlanId && (
                              <div className="absolute top-4 right-4 bg-amber-600 text-white rounded-full w-8 h-8 flex items-center justify-center">
                                <CheckOutlined style={{ fontSize: '14px' }} />
                              </div>
                            )}

                            {/* Plan Header */}
                            <div className="mb-6">
                              <h3 className="text-xl font-bold text-gray-900 mb-2">
                                {plan.planName}
                              </h3>
                              {plan.description && (
                                <p className="text-gray-600 text-sm mb-4">
                                  {plan.description}
                                </p>
                              )}

                              <div className="mb-2">
                                <div className="flex items-baseline gap-2">
                                  {plan.displayAmount &&
                                    plan.displayAmount > plan.amount && (
                                      <span className="text-md text-gray-400 line-through">
                                        {formatMoney(plan.displayAmount, plan.currency)}
                                      </span>
                                    )}
                                  <span className="text-3xl font-bold text-gray-900">
                                    {formatMoney(plan.amount, plan.currency)}
                                  </span>
                                  <span className="text-gray-600 text-sm">
                                    /{cycleSuffix}
                                  </span>
                                </div>
                                {plan.identifier !== 'monthly' && (
                                  <p className="text-gray-600 text-sm mt-1 font-medium">
                                    {formatMoney(perMonthEquiv, plan.currency)}/month, {billingText.toLowerCase()}
                                  </p>
                                )}

                                {savings > 0 && (
                                  <div className="mt-2">
                                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                                      {Math.round(savings)}% OFF
                                    </span>
                                  </div>
                                )}

                                {plan.save != null && plan.save > 0 && (
                                  <div className="mt-2">
                                    <span className="text-green-700 text-xs font-medium">
                                      Save {formatMoney(plan.save, plan.currency)}/{cycleSuffix}
                                    </span>
                                  </div>
                                )}
                              </div>
                              {plan.identifier === 'monthly' && (
                                <p className="text-xs text-gray-500">{billingText}</p>
                              )}
                            </div>

                            {/* Main Feature Descriptions */}
                            {mainDescriptions.length > 0 && (
                              <div className="mb-4">
                                <ul className="space-y-3">
                                  {mainDescriptions.map((desc) => (
                                    <li
                                      key={desc.id}
                                      className="flex items-start gap-3"
                                    >
                                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center mt-0.5">
                                        <CheckOutlined
                                          style={{
                                            fontSize: '10px',
                                            color: '#ea580c',
                                          }}
                                        />
                                      </div>
                                      <span className="text-sm text-gray-700 leading-relaxed">
                                        {desc.description}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Addon Feature Descriptions */}
                            {addonDescriptions.length > 0 && (
                              <div className="mb-6 flex-grow">
                                <ul className="space-y-3">
                                  {addonDescriptions.map((desc) => (
                                    <li
                                      key={desc.id}
                                      className="flex items-start gap-3"
                                    >
                                      <PlusCircleOutlined
                                        className="flex-shrink-0 mt-0.5"
                                        style={{
                                          fontSize: '18px',
                                          color: '#ea580c',
                                        }}
                                      />
                                      <span className="text-sm text-gray-700 leading-relaxed">
                                        {desc.description}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Action Button */}
                            <button
                              className={getButtonClasses(plan.id)}
                              disabled={plan.id === currentPlanId}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (plan.id === currentPlanId) return;
                                handlePlanSelect(plan.id);
                              }}
                            >
                              {getButtonText(plan)}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
            )}
            <p className="text-xs text-gray-500 text-center" data-testid="tax-note">
              {/* GST is an Indian tax, so it is only named for INR prices. */}
              {selectedCurrency === 'INR'
                ? 'All prices include GST.'
                : 'All prices include applicable tax.'}
            </p>
          </div>

          {/* Order Summary Sidebar */}
          <div className="w-full lg:w-96">
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm sticky top-6">
              <h3 className="font-semibold text-lg text-gray-900 mb-4">
                {isPlanChange ? 'Change Summary' : 'Order Summary'}
              </h3>
              <hr className="border-gray-300 mb-6" />

              {/* What the server says this change will do. Shown before the
                  price so the customer reads "when" before "how much". */}
              {previewLoading && (
                <div className="text-sm text-gray-500 mb-4">
                  Checking what this change would do...
                </div>
              )}

              {preview && !previewLoading && (
                <div className="mb-6 rounded-lg bg-gray-50 border border-gray-200 p-4">
                  <p className="text-sm font-semibold text-gray-900 mb-1">
                    {preview.currentPlan.planName} &rarr;{' '}
                    {preview.targetPlan.planName}
                  </p>

                  <p className="text-xs text-gray-600 mb-3">
                    {preview.effective === 'immediate'
                      ? 'Starts as soon as your payment is confirmed'
                      : `Starts on ${preview.effectiveDate}`}
                  </p>

                  {preview.effective === 'at_period_end' && (
                    <p className="text-xs text-gray-700 mb-2">
                      Nothing to pay today. You keep your current plan until{' '}
                      {preview.currentPeriodEnd}.
                    </p>
                  )}

                  {preview.warnings.map((warning) => (
                    <p key={warning} className="text-xs text-gray-600 mb-1">
                      {warning}
                    </p>
                  ))}

                  {!preview.autoRenewAfterChange && (
                    <p className="text-xs text-amber-700 mt-2">
                      This plan will not renew automatically. We will remind you
                      before it ends.
                    </p>
                  )}

                  {/* A downgrade takes things away, so say which. */}
                  {preview.changeKind === 'downgrade' &&
                    preview.limitChanges.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-900 mb-1">
                          Limits that will change
                        </p>
                        {preview.limitChanges.map((limit) => (
                          <p key={limit.key} className="text-xs text-gray-600">
                            {limit.key}: {limit.from ?? 'unlimited'} &rarr;{' '}
                            {limit.to ?? 'unlimited'}
                          </p>
                        ))}
                      </div>
                    )}
                </div>
              )}

              {isQuoteLoading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="text-gray-600">Loading order summary...</div>
                </div>
              ) : quote ? (
                <>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="font-semibold text-sm text-gray-900">
                        {quote.planName}
                      </p>
                      <p className="text-xs text-gray-900">
                        {quote.billingCycle === 'yearly' ? 'Yearly' : quote.billingCycle === 'quarterly' ? 'Quarterly' : 'Monthly'} Subscription
                      </p>
                    </div>
                    <p className="font-semibold text-sm text-gray-900">
                      {quote.currency}&nbsp;{quote.gross}
                    </p>
                  </div>

                  {quote.discount && parseFloat(quote.discount) > 0 && (
                    <div className="flex justify-between items-center mb-4">
                      <p className="text-sm text-green-600">Discount ({quote.coupon?.code})</p>
                      <p className="text-sm text-green-600">
                        -{quote.currency}&nbsp;{quote.discount}
                      </p>
                    </div>
                  )}

                  <hr className="border-gray-300 mb-6" />

                  <div className="flex justify-between items-center mb-6">
                    <p className="font-semibold text-sm text-gray-900">Total Today</p>
                    <p className="font-bold text-lg text-gray-900">
                      {quote.currency}&nbsp;{quote.net}
                    </p>
                  </div>
                  {currentPlanInfo && (
                    <p className="text-xs text-gray-500 mb-6 text-right">
                      Next billing date - {currentPlanInfo.billingDate}
                    </p>
                  )}
                </>
              ) : currentPlanInfo ? (
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="font-semibold text-sm text-gray-900">
                      {currentPlanInfo.name}
                    </p>
                    <p className="text-xs text-gray-900">
                      {subscriptionCycle === 'yearly' ? 'Yearly' : subscriptionCycle === 'quarterly' ? 'Quarterly' : 'Monthly'} Subscription
                    </p>
                  </div>
                  <p className="font-semibold text-sm text-gray-900">
                    {formatMoney(currentPlanInfo.price, currentPlanInfo.currency)}
                  </p>
                </div>
              ) : (
                <div className="text-sm text-gray-500 mb-4">Please select a plan</div>
              )}

              {/* Offer Code Input */}
              <Divider />
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Apply Offer Code</h4>
                {canQuote ? (
                  <CouponInput
                    onApply={(code) => setAppliedCouponCode(code)}
                    onRemove={() => setAppliedCouponCode(undefined)}
                    appliedCoupon={quote?.coupon}
                    couponError={quote?.couponError}
                    isLoading={isQuoteLoading}
                    currency={quote?.currency}
                    discount={quote?.discount}
                  />
                ) : (
                  // Better to say offer codes are unavailable than to show a box
                  // that accepts a code and silently discounts nothing.
                  <p className="text-sm text-gray-500">
                    Offer codes are temporarily unavailable. You can still
                    subscribe at the listed price.
                  </p>
                )}
              </div>

              <div className="my-3">
                <Button
                  type="primary"
                  className="w-full mt-6 !rounded-lg"
                  size="large"
                  onClick={handleSubscribe}
                  disabled={
                    !currentPlanInfo ||
                    isQuoteLoading ||
                    previewLoading ||
                    submitting ||
                    selectedPlan === currentPlanId ||
                    // A plan change is priced by its own preview, so it does
                    // not wait on a checkout quote.
                    (!isPlanChange && canQuote && !quote)
                  }
                  loading={createSubscription.isPending || submitting}
                >
                  {!currentPlanInfo
                    ? 'Select a Plan'
                    : preview?.effective === 'at_period_end'
                      ? `Schedule change to ${currentPlanInfo.name}`
                      : isPlanChange
                        ? `Upgrade to ${currentPlanInfo.name}`
                        : `Subscribe to ${currentPlanInfo.name}`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
